/**
 * Два зеркала в одной ленте: кассы и дисциплины считаются как один рынок.
 *
 * `meta.sources` на этом контуре — `ch_a` и `ch_v`, не конторы. Шапка, сито и
 * студия, которые на v1 читают источники как площадки, здесь обязаны смотреть
 * ноги. Иначе «2 площадки в эфире» и пустой список контор (18.09).
 */

import { UNNAMED_VENUE, type Fork } from '../lib/protocol';
import { sportCanon } from '../lib/sports';

import { venueCanon } from './venues';
import type { Meta } from './meta';

const SOURCE_CODES = new Set(['ch_a', 'ch_b', 'ch_v', 'ch_c', 'ch_bb']);

const STALE_MIR: Record<string, number> = { ch_a: 1, ch_b: 2, ch_v: 3, ch_bb: 5 };

export function isSourceCode(code: string): boolean {
  return SOURCE_CODES.has(code);
}

/** Касса с ноги: алиас и отсев псевдоисточников. */
export function bookOf(code: string | undefined): string {
  const canon = venueCanon((code ?? '').trim());
  if (canon === '' || canon === UNNAMED_VENUE || isSourceCode(canon)) return '';
  return canon;
}

const RUB_BOOKS = new Set([
  'fonbet',
  'leon',
  'winline',
  'zenit',
  'olimp',
  'betboom',
  'bingoboom',
  'betcity',
  'ligastavok',
  'marathon',
  'pinup',
  'pari',
  'mostbet',
  'onexbetru',
]);

const PREDICT = new Set([
  'polymarket',
  'kalshi',
  'predictfun',
  'limitless',
  'sxbet',
  'azuro',
  'myriad',
  'cdna',
  'smarkets',
  'welstroy',
  'opinion',
]);

const CRYPTO = new Set([
  'ggbet',
  'thunderpick',
  'bcgame',
  'cloudbet',
  'csgopositive',
  'vbet',
  'welstroy',
  '5051x',
  'stake',
  'roobet',
  'rainbet',
  'shuffle',
  'duelbits',
  'sportsbet',
  'rollbit',
  'rivalry',
  'onexbet',
  '1xstavka',
  'jetton',
  'riobet',
  'duel',
  'lootbet',
  'betinasia',
  'bovada',
  'pinnacle',
  'dafabet',
  'cbet',
  'dexsport',
  'buffbet',
  'egb',
  'freewin',
  'bxyz',
  'sharpxch',
]);

export function isRubBook(code: string | undefined): boolean {
  const canon = bookOf(code);
  return canon !== '' && RUB_BOOKS.has(canon);
}

function deskKind(code: string): 'rub' | 'predict' | 'crypto' {
  const canon = bookOf(code);
  if (canon === '' || RUB_BOOKS.has(canon)) return 'rub';
  if (PREDICT.has(canon)) return 'predict';
  if (CRYPTO.has(canon)) return 'crypto';
  return 'rub';
}

/** Зарубежные пары, включая предикт-предикт. Рублёвая нога — нет. */
export function deskPairOk(fork: Pick<Fork, 'legs'>): boolean {
  const books = (fork.legs ?? []).map((leg) => bookOf(leg.s)).filter(Boolean);
  if (books.length < 2) return false;
  const kinds = new Set(books.map((code) => deskKind(code)));
  if (kinds.has('rub')) return false;
  return true;
}

/** BetBurger с /tests: предикт×предикт остаётся. Рубль и одна касса — нет. */
export function feedPairOk(fork: Fork): boolean {
  if (forkMir(fork) !== 5) return deskPairOk(fork);
  const books = (fork.legs ?? []).map((leg) => bookOf(leg.s)).filter(Boolean);
  if (books.length < 2) return false;
  if (books[0] === books[1]) return false;
  return !books.some((code) => deskKind(code) === 'rub');
}

/** The RU feed has its own venue pool; two RUB bookmakers are allowed. */
export const RU_BOOKS = ['onexbetru', 'betboom', 'leon', 'fonbet', 'winline', 'olimp', 'polymarket', 'kalshi'] as const;
export const isRuBook = (code: string): boolean => (RU_BOOKS as readonly string[]).includes(bookOf(code));

export function ruPairOk(fork: Pick<Fork, 'legs'>): boolean {
  const books = (fork.legs ?? []).map((leg) => bookOf(leg.s));
  return books.length === 2 && books[0] !== books[1] && books.every(isRuBook);
}

const SPORT_HINTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/cs2|cs[:\s-]*go|counter[\s-]*strike/i, 'cs2'],
  [/dota[\s-]*2|\bdota2\b/i, 'dota2'],
  [/valorant/i, 'valorant'],
  [/league[\s-]*of[\s-]*legends|\blol\b/i, 'lol'],
];

function sportHint(fork: Pick<Fork, 'sport' | 'league' | 'legs'>): string {
  const blob = [
    typeof fork.league === 'string' ? fork.league : '',
    ...(fork.legs ?? []).map((leg) => (typeof leg.url === 'string' ? leg.url : '')),
  ].join(' ');
  for (const [hint, code] of SPORT_HINTS) {
    if (hint.test(blob)) return code;
  }
  return '';
}

export function sportOf(fork: Pick<Fork, 'sport' | 'league' | 'legs'>): string {
  const raw = sportCanon(typeof fork.sport === 'string' ? fork.sport : '');
  if (raw !== '' && raw !== 'esports') return raw;
  return sportHint(fork) || raw;
}

function staleMirrors(meta: Meta): Set<number> {
  const out = new Set<number>();
  for (const source of meta.sources) {
    if (!source.stale) continue;
    const mir = STALE_MIR[source.code];
    if (mir !== undefined) out.add(mir);
  }
  return out;
}

function forkMir(fork: Fork): number | null {
  const raw = fork.mir;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Живые кассы обоих зеркал, без двойного счёта одной конторы. */
export function booksInAir(forks: Iterable<Fork>, meta: Meta): string[] {
  const stale = staleMirrors(meta);
  const books = new Set<string>();
  for (const fork of forks) {
    const mir = forkMir(fork);
    if (mir !== null && stale.has(mir)) continue;
    for (const leg of fork.legs ?? []) {
      const code = bookOf(leg.s);
      if (code !== '') books.add(code);
    }
  }
  return [...books].sort();
}

function nameKey(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9а-яё]+/gi, '');
}

function whoOf(fork: Fork): string {
  const raw = fork.participants ?? fork.who;
  const names = Array.isArray(raw)
    ? raw.filter((one): one is string => typeof one === 'string')
    : [];
  if (names.length === 0) {
    const first = fork.legs?.[0]?.participants ?? [];
    return first.map(nameKey).filter(Boolean).sort().join('|');
  }
  return names.map(nameKey).filter(Boolean).sort().join('|');
}

function familyLine(fork: Fork): string {
  const key = typeof fork.market === 'string' ? fork.market : '';
  const parts = key.split(':');
  const family = (parts[0] ?? '').toUpperCase();
  const side = (parts[3] ?? '').toUpperCase();
  const line = (parts[4] ?? '').replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  return `${family}:${side}:${line}`;
}

/** Событие без серверного `ev`: один матч + семья рынка, без линии и стороны. */
export function foldKey(fork: Fork): string {
  const who = whoOf(fork);
  const family = (typeof fork.market === 'string' ? (fork.market.split(':')[0] ?? '') : '').toUpperCase();
  if (who === '' || family === '') return '';
  return `${sportOf(fork)}|${who}|${family}`;
}

/** Одна вилка с двух зеркал: дисциплина, состав, кассы, семья рынка. */
export function twinKey(fork: Fork): string {
  const books = (fork.legs ?? [])
    .map((leg) => bookOf(leg.s))
    .filter(Boolean)
    .sort()
    .join('+');
  return `${sportOf(fork)}|${whoOf(fork)}|${books}|${familyLine(fork)}`;
}

function roiOf(fork: Fork): number {
  const raw = fork.roi;
  if (typeof raw !== 'string' && typeof raw !== 'number') return Number.NEGATIVE_INFINITY;
  const n = Number(raw);
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

/** Оставить одну строку из пары зеркал. Больше ROI, при равенстве — канал A. */
export function betterTwin(left: Fork, right: Fork): Fork {
  const a = roiOf(left);
  const b = roiOf(right);
  if (a !== b) return a > b ? left : right;
  const mirA = forkMir(left) ?? 99;
  const mirB = forkMir(right) ?? 99;
  return mirA <= mirB ? left : right;
}

export function keepTwin<T extends { id: string }>(
  rows: readonly T[],
  forkOf: (row: T) => Fork,
): T[] {
  const best = new Map<string, T>();
  for (const row of rows) {
    const fork = forkOf(row);
    const key = twinKey(fork);
    const parts = key.split('|');
    if ((parts[1] ?? '') === '' && (parts[2] ?? '') === '') {
      best.set(`id:${row.id}`, row);
      continue;
    }
    const seen = best.get(key);
    if (seen === undefined) {
      best.set(key, row);
      continue;
    }
    const seenMir = forkMir(forkOf(seen));
    const nextMir = forkMir(fork);
    if (seenMir !== null && nextMir !== null && seenMir === nextMir) {
      best.set(`id:${row.id}`, row);
      continue;
    }
    // Строка BetBurger жила на /tests рядом с тем же матчем других зеркал.
    if (seenMir === 5 || nextMir === 5) {
      best.set(`id:${row.id}`, row);
      continue;
    }
    best.set(key, betterTwin(forkOf(seen), fork) === fork ? row : seen);
  }
  const winners = new Set([...best.values()].map((row) => row.id));
  return rows.filter((row) => winners.has(row.id));
}
